// The one place the browser talks to riksu.
//
// # It talks to the BFF, never to riksu
//
// Same origin, so there is no base URL to configure, no CORS to get right, and no
// token in this process to steal. The session is an `httpOnly` cookie the BFF set,
// which this code cannot read and does not need to — `credentials: "same-origin"`
// is the whole of it. See ADR-0032.
//
// # Errors are RFC 9457, so there is one shape and one renderer
//
// Riksu answers every failure with `application/problem+json`. A screen that
// hand-parses an error body is a screen that renders `[object Object]` the first
// time somebody adds a field, so the parsing happens once, here, and every caller
// gets a `Problem`.
//
// # The idempotency key is minted per INTENT, not per request
//
// Riksu requires `Idempotency-Key` on every write and replays the stored response
// for a repeat. That only protects anybody if the key is *stable across the retry*,
// and the retry a user actually performs is **F5**. A key minted inside `fetch` is
// a fresh key on reload, which turns a reload into a second leave request.
//
// So a key belongs to the *intent* — "this leave request, the one this form is
// open for" — and lives in `sessionStorage` until the write succeeds.
//
// # TWO IDENTICAL READS IN FLIGHT AT ONCE ARE ONE READ
//
// Nothing joins two components that happen to want the same thing. `PersonRecord`
// builds the pane header from `GET /v1/people/{id}` and renders `Profile`, which reads
// the same record for its own six surfaces — measured 1.5 ms apart, both in flight
// together, both arriving. Neither is wrong and neither can be deleted without one
// component reaching into the other's state, so the join belongs HERE: while a GET is
// out, a second GET for the same URL is handed the SAME answer rather than a second
// round trip. Measured against the running stack, opening one person record made 18
// requests where 9 questions were asked.
//
// Four rules make that safe rather than merely cheap, and each of them is a way this
// goes wrong if it is skipped:
//
// - **A GET, and only a GET.** A write is not idempotent to collapse — riksu requires
//   `Idempotency-Key` on every one of them for exactly this reason — and two POSTs that
//   look alike are two things somebody meant to do.
// - **THE ENTRY DIES WHEN THE REQUEST SETTLES**, success or failure. An entry that
//   outlived its request would be a cache, and a cache is a different decision with a
//   different consequence: it goes stale after a write, and deciding when it does is a
//   product question rather than a transport one. It is not made here.
// - **Every caller gets its own COPY of the answer**, always — not only when the answer
//   turned out to be shared. A screen that may or may not be mutating its neighbour's
//   row depending on how two mounts happened to interleave is a defect that reproduces
//   one time in ten.
// - **One caller walking away is not everybody walking away.** The caller's own
//   `AbortSignal` detaches that caller and nothing else; the request itself is cancelled
//   only when the LAST one has gone. `useQuotation` abandons a period the person moved
//   on from, and it may not abandon the pane that asked the same question beside it.
//
// A refusal is shared on the same terms: both callers see the one `ApiError`, and the
// entry clears, so a caller that reacts by re-reading gets a new request rather than the
// corpse of the one that just failed.

import type { components } from "./contract";

export type { components, operations, paths } from "./contract";

/** RFC 9457 problem details, as riksu's contract declares them. */
export type Problem = components["schemas"]["Error"];

/** Where a write's idempotency keys live between a submit and a reload. */
const INTENT_STORE = "riksu.intent";

/**
 * Thrown for any non-2xx. Carries the parsed problem rather than a string, because
 * `detail` is written for a person and `type`/`status` are what code branches on.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(status: number, problem: Problem) {
    // `detail` is the human sentence riksu wrote. The refusals in this system are
    // deliberately written to be shown — several of them name what *would* have
    // worked — so re-wording them here would throw away the useful half.
    super(problem.detail ?? problem.title ?? `request failed with ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }

  /** A retry of the same key while the original is still running (409). */
  get isInFlight(): boolean {
    return this.status === 409;
  }
}

/**
 * The stable key for one user intent.
 *
 * `id` names the intent and not the request: an open leave-request form is one
 * intent no matter how many times it is submitted, reloaded or resumed. Call it
 * when the form opens, keep the value while the form is open, and call `intentDone`
 * when the write succeeds.
 *
 * A page reload re-runs this with the same `id` and gets the same key back, which
 * is the entire point — riksu then replays the original response instead of opening
 * a second process.
 */
export function intentKey(id: string): string {
  const store = readIntents();
  const existing = store[id];
  if (existing) {
    return existing;
  }
  const minted = crypto.randomUUID();
  store[id] = minted;
  writeIntents(store);
  return minted;
}

/** Forgets an intent's key. Call it once the write has succeeded. */
export function intentDone(id: string): void {
  const store = readIntents();
  if (store[id] === undefined) {
    return;
  }
  delete store[id];
  writeIntents(store);
}

/** An uncertain write may be retried with its original key after a reload. */
export function intentPending(id: string): boolean {
  return Object.hasOwn(readIntents(), id);
}

function readIntents(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(INTENT_STORE);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    // Private mode, a quota, or a corrupted value. Losing the map means a reload
    // mints a new key and riksu opens a second process — bad, and still better than
    // refusing to let anybody submit anything.
    return {};
  }
}

/** Forget only this working-copy namespace, retaining the active owner's intents. */
export function intentDoneWithin(
  prefix: string,
  keep: string | ((intent: string) => boolean) | null = null,
): void {
  const store = readIntents();
  let changed = false;
  for (const id of Object.keys(store)) {
    const retained = typeof keep === "function" ? keep(id) : keep !== null && id.startsWith(keep);
    if (id.startsWith(prefix) && !retained) {
      delete store[id];
      changed = true;
    }
  }
  if (changed) writeIntents(store);
}

function writeIntents(store: Record<string, string>): void {
  try {
    sessionStorage.setItem(INTENT_STORE, JSON.stringify(store));
  } catch {
    // Same reasoning as above.
  }
}

/** Told that a write SUCCEEDED, with the method and the path as the caller spelled it. */
type Wrote = (method: string, path: string) => void;

const wrote = new Set<Wrote>();

/**
 * Subscribes to successful writes.
 *
 * THE REASON THIS IS IN THE TRANSPORT and not in the read layer: every write in the product
 * already goes through `call`, so on the day the read store lands, all of them announce
 * themselves and none of them is edited. A read that has moved to `useRead` is therefore
 * correctly re-read by a write in a screen nobody has touched — which is what lets the two
 * mechanisms live side by side while screens migrate one at a time, instead of a flag day.
 *
 * `api.ts` exports this and imports nothing for it, so the transport keeps its independence
 * and there is no cycle: the read layer knows about the transport, never the reverse.
 */
export function onWrite(listener: Wrote): () => void {
  wrote.add(listener);
  return () => {
    wrote.delete(listener);
  };
}

/** What a caller passes; `intent` is required on every write and absent on reads. */
export interface Request {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  /** The intent id for a write. */
  readonly intent?: string;
  readonly signal?: AbortSignal;
}

/**
 * A read that has left the browser and not yet come back.
 *
 * `waiting` is what makes an abort a per-caller act: it counts the callers still
 * interested, and only the last one out cancels the request everybody had joined.
 */
interface Shared {
  /** The one request, settling once for every caller that joined it. */
  readonly answer: Promise<unknown>;
  /** Cancels it — pulled only when `waiting` reaches zero. */
  readonly controller: AbortController;
  /** How many callers are still waiting for this answer. */
  waiting: number;
}

/**
 * The reads in flight right now, keyed by METHOD AND URL.
 *
 * **This is not a cache**: an entry is created when a request leaves and removed when it
 * settles, so nothing here can answer a question the server has not been asked. See the
 * header of this file for why that line is where it is.
 *
 * The key is the URL as the caller SPELLED it, not a normalised address. Two spellings
 * of one resource are two keys and cost two requests — which is the honest failure
 * direction: normalising here would mean this file deciding that
 * `?a=1&b=2` and `?b=2&a=1` are the same question, and being wrong about that hands one
 * caller another caller's answer.
 */
const sharing = new Map<string, Shared>();

/**
 * Calls the BFF and returns the parsed body.
 *
 * Two identical GETs in flight at once are ONE request and two answers; see the header.
 *
 * @throws {ApiError} on any non-2xx, carrying the parsed problem.
 */
export async function call<T>(path: string, request: Request = {}): Promise<T> {
  const method = request.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };

  if (request.body !== undefined) {
    headers["content-type"] = "application/json";
  }
  if (method !== "GET") {
    if (request.intent === undefined) {
      // Loud, and at the call site rather than as a 400 from riksu. A write with no
      // intent is a write whose retry opens a second one, and that is a defect in
      // this file's caller rather than something for the server to refuse.
      throw new Error(
        `${method} ${path} needs an intent id: a write's idempotency key must ` +
          "survive a reload, so it belongs to the form and not to this call",
      );
    }
    headers["idempotency-key"] = intentKey(request.intent);

    // A WRITE ENDS EVERY SHARE, and this is the one line that keeps the join above from
    // being able to answer with something that predates a change. The shape it exists
    // for is `flow.tsx`'s `created()`: a create lands and the source is re-read
    // immediately — and if some other component's read of that same list happened to be
    // in flight when the write went out, the re-read would join it and render the list
    // as it was BEFORE the thing that was just created. Cleared on the way out as well
    // as on the way in, because the window that matters is the one between the two.
    //
    // Callers that had already joined keep the answer they were waiting for. Their
    // request predates the write exactly as it did before this file shared anything.
    sharing.clear();
    try {
      return await send<T>(path, method, headers, request.body, request.signal);
    } finally {
      sharing.clear();
    }
  }

  return join<T>(`${method} ${path}`, request.signal, (shared) =>
    send<unknown>(path, method, headers, request.body, shared),
  );
}

/** A non-mutating POST whose input belongs in a body, such as an AI chat prompt.
 * No idempotency key is minted because the operation stores no business change.
 */
export function query<T>(
  path:
    | "/v1/ai/chat"
    | "/v1/ai/company-news/rewrite"
    | "/v1/ai/requisition-reports/analysis"
    | "/v1/recruiting/requisition-report-template-imports/validations"
    | "/v1/expense/report-template-imports/validations"
    | "/v1/expense/report-distribution/previews"
    | "/v1/recruiting/posting-imports/validations"
    | "/v1/onboarding/mentor-imports/validations"
    | "/v1/performance/probation-draft-imports/validations"
    | "/v1/leave/type-imports/validations"
    | "/v1/payroll/result-imports/validations"
    | "/v1/payroll/result-comparisons"
    | `/v1/payroll/result-imports/${string}/review-preparations`
    | `/v1/payroll/run-reviews/${string}/publication-preparations`
    | "/v1/expense/item-review-quotations"
    | `/v1/ai/requisition-work/${string}/summary`
    | `/v1/ai/company-policies/${string}/summary`
    | `/v1/ai/expense/claims/${string}/receipts/${string}/analysis`
    | `/v1/ai/position-descriptions/${string}/clarification`
    | `/v1/ai/performance-goals/${string}/clarification`
    | `/v1/ai/performance-review-assignments/${string}/writing`
    | `/v1/ai/performance-review-assignments/${string}/bias-check`,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> {
  return send<T>(
    path,
    "POST",
    { accept: "application/json", "content-type": "application/json" },
    body,
    signal,
  );
}

/**
 * Joins the request in flight for `key`, or starts it.
 *
 * The caller's own signal never reaches `fetch`. It detaches THIS caller, and the
 * request is cancelled only once nobody is left waiting for it.
 */
function join<T>(
  key: string,
  signal: AbortSignal | undefined,
  begin: (shared: AbortSignal | undefined) => Promise<unknown>,
): Promise<T> {
  // A caller that has already given up starts nothing and joins nothing — the same
  // answer `fetch` gives an aborted signal, so nothing downstream sees a difference.
  if (signal?.aborted) {
    return Promise.reject(abandoned(signal));
  }

  let entry = sharing.get(key);
  if (entry === undefined) {
    const controller = new AbortController();
    // A caller with no signal cannot walk away before the read settles, so attaching an
    // AbortSignal buys nothing. Keeping it off also matters in non-browser harnesses:
    // fetch implementations correctly reject a signal created by another JavaScript
    // realm even though both objects are named AbortSignal.
    const started: Shared = {
      answer: begin(signal === undefined ? undefined : controller.signal),
      controller,
      waiting: 0,
    };
    // THE ENTRY DIES WITH THE REQUEST. Attached before any caller's handlers, so the
    // table is already clear by the time anybody sees the answer: a caller that reacts
    // to a refusal by re-reading must get a new request rather than the one that just
    // failed.
    const forget = () => {
      if (sharing.get(key) === started) {
        sharing.delete(key);
      }
    };
    started.answer.then(forget, forget);
    // Nobody need be left listening when the last caller walks away, and a rejection
    // with no audience is an UNHANDLED one — which vitest fails a run for and a browser
    // logs as an error nobody can trace. This is the audience of last resort; it is not
    // what any caller sees.
    started.answer.catch(() => undefined);
    sharing.set(key, started);
    entry = started;
  }

  const shared = entry;
  shared.waiting += 1;

  return new Promise<T>((resolve, reject) => {
    const answered = (body: unknown) => resolve(copyOf(body) as T);

    if (signal === undefined) {
      shared.answer.then(answered, reject);
      return;
    }

    const walkAway = () => {
      shared.waiting -= 1;
      // THE LAST ONE OUT CANCELS IT. While anybody else is still waiting, their request
      // is untouched — one caller abandoning a question is not everybody abandoning it,
      // which is the whole reason the caller's signal never reached `fetch`. And the
      // entry goes with the cancellation, so the next caller starts a fresh request
      // rather than joining one that is being torn down.
      if (shared.waiting === 0) {
        if (sharing.get(key) === shared) {
          sharing.delete(key);
        }
        shared.controller.abort(signal.reason);
      }
      reject(abandoned(signal));
    };

    signal.addEventListener("abort", walkAway, { once: true });
    shared.answer.then(
      (body) => {
        signal.removeEventListener("abort", walkAway);
        answered(body);
      },
      (failure: unknown) => {
        signal.removeEventListener("abort", walkAway);
        reject(failure);
      },
    );
  });
}

/**
 * What a caller that gave up is rejected with.
 *
 * `fetch`'s own answer, so a caller branching on an abort sees no difference between a
 * request it cancelled alone and one it merely detached from.
 */
function abandoned(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException("The operation was aborted.", "AbortError");
}

/**
 * The caller's own copy of a parsed body.
 *
 * `structuredClone` rather than a JSON round trip: the round trip turns an `undefined`
 * member into a missing one, which silently rewrites what riksu sent — and a 204 answers
 * with `undefined` itself.
 */
function copyOf<T>(body: T): T {
  return body === undefined ? body : (structuredClone(body) as T);
}

/**
 * One request, from the headers to the parsed body.
 *
 * Separated from `call` so the sharing above has something to hand a signal that is not
 * the caller's.
 */
async function send<T>(
  path: string,
  method: string,
  headers: Record<string, string>,
  body: unknown,
  signal: AbortSignal | undefined,
): Promise<T> {
  const response = await fetch(path, {
    method,
    headers,
    // The session cookie, and nothing else. Never `include`: this is same-origin by
    // design, and `include` would start sending credentials to a cross-origin host
    // the day somebody points this at one.
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new ApiError(response.status, await problemOf(response));
  }
  // AFTER the refusal above, so a write riksu rejected invalidates nothing. A read dropped
  // because of a write that did not happen is a table that empties itself on a validation
  // error.
  if (method !== "GET") {
    for (const listener of wrote) {
      listener(method, path);
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * The problem body, or a synthesised one.
 *
 * A gateway timeout or a proxy error is not written by riksu and carries no
 * problem+json, so the shape is filled in rather than left for the renderer to
 * discover. The title says it failed *before riksu answered*, because "something
 * went wrong" sends somebody to read riksu's logs for an error riksu never saw.
 */
async function problemOf(response: Response): Promise<Problem> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/problem+json")) {
    try {
      return (await response.json()) as Problem;
    } catch {
      // Fall through to the synthesised one.
    }
  }
  return {
    type: "about:blank",
    title: `The request failed before riksu answered (${response.status})`,
    status: response.status,
  } as Problem;
}
